<template>
  <BlockFieldset class="project-clarification">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t('deferredState')"
          label-for="deferredState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            :placeholder="$t('deferredState')"
            v-model="project.deferredState.deferred"
            :options="yesNo()"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
            @select="onChangeProjectDeferredState"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="yesNoStateReversedColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="yesNoStateReversedColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          v-if="hasDeferredDetails"
          horizontal
          :label="$t('reason')"
          label-for="reason"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <div class="form-control-plaintext">
            {{ $t(project.deferredState.reason) }}
          </div>
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <b-form-group
          v-if="hasDeferredDetails"
          horizontal
          :label="$t('description')"
          label-for="deferredDescription"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Textarea id="deferredDescription" v-model="project.deferredState.description" rows="3" />
        </b-form-group>

        <b-form-group
          v-if="hasDeferredDetails"
          horizontal
          :label="$t('comment')"
          label-for="deferredComment"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Textarea id="deferredComment" v-model="project.deferredState.comment" rows="3" />
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="hasSaveButton" class="text-right mt-2">
      <b-button variant="bg" :disabled="isSaving" @click="save">
        {{ $t('saveNamed', { name: $t('deferredState') }) }}
      </b-button>
    </div>

    <Dialog
      :header="$t('reasonRequired')"
      :visible.sync="displayStateChangeConfirmation"
      :contentStyle="{ overflow: 'visible' }"
      :modal="true"
    >
      <div>
        <div class="flex-row">
          <div>{{ $t('reason') }}</div>
          <div>
            <Dropdown
              v-model="project.deferredState.reason"
              :options="getEnumValues('DeferredReason')"
              :placeholder="$t('select')"
            >
              <template #option="slotProps">
                <b-badge>
                  {{ $t(slotProps.option) }}
                </b-badge>
              </template>
              <template #value="slotProps">
                <b-badge>
                  {{ $t(slotProps.value) }}
                </b-badge>
              </template>
            </Dropdown>
          </div>
        </div>

        <div class="flex-row">
          <div>{{ $t('description') }}</div>
          <div>
            <Textarea v-model="project.deferredState.description" rows="3" cols="40" />
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          class="btn btn-narrow btn-default ml-2 mb-2"
          @click="onCancelProjectDeferredStateChangeReason"
        >
          {{ $t('cancel') }}
        </Button>
        <Button
          class="btn btn-narrow btn-inverse ml-2 mb-2"
          @click="onSaveProjectDeferredStateChangeReason"
          :disabled="deferredStateOkButtonDisabled"
        >
          {{ $t('ok') }}
        </Button>
      </template>
    </Dialog>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import { yesNoStateReversedColor } from '@/helpers/colors';
import { yesNo } from '@/helpers/enums';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { DEFAULT_DEFERRED_STATE, toProjectClarificationInput } from './projectClarificationInput';

/** Number of the unsaved project (ProjectEdit `_new`): its values go with `createProject`. */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block project-clarification (plan docs/work/_done/2026-10-project-block-clarification): "In Klärung"
 * with reason, description and comment, saved through its own mutation with its own button.
 * Changing to "yes" opens the dialog for the reason. A project without `deferredState` starts
 * from the defaults. On the unsaved project it has no save button. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectClarification',
  components: {
    BlockFieldset,
    Multiselect,
    Dialog,
    Dropdown,
    Textarea,
  },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return {
      displayStateChangeConfirmation: false,
    };
  },
  computed: {
    ...mapGetters(['getEnumValues']),
    blockInput() {
      return toProjectClarificationInput(this.project);
    },
    hasSaveButton() {
      return !this.isBlockReadOnly && this.project.number !== NEW_PROJECT_NUMBER;
    },
    /** Reason, description and comment: when deferred or when something was written. */
    hasDeferredDetails() {
      const { deferred, description, comment } = this.project.deferredState;
      return deferred === 'YES' || description !== '' || comment !== '';
    },
    deferredStateOkButtonDisabled() {
      return this.project.deferredState.description === '';
    },
  },
  watch: {
    // Runs after the baseline of BlockSaveMixin, which already uses the same defaults.
    project: {
      handler: 'initDeferredState',
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['updateProjectClarification', 'fetchEnumValues']),
    saveBlockInput(input) {
      return this.updateProjectClarification({ projectNumber: this.project.number, input });
    },
    /** A project without `deferredState` gets a copy of the defaults to edit. */
    initDeferredState() {
      if (!this.project.deferredState) {
        this.$set(this.project, 'deferredState', { ...DEFAULT_DEFERRED_STATE });
      }
    },
    /**
     * if project.deferredState is changed to yes a reason has to be given
     * @param {*} newState
     */
    onChangeProjectDeferredState(newState) {
      if (newState === 'YES') {
        this.displayStateChangeConfirmation = true;
      }
    },
    onCancelProjectDeferredStateChangeReason() {
      this.project.deferredState.deferred = 'NO';
      this.displayStateChangeConfirmation = false;
    },
    onSaveProjectDeferredStateChangeReason() {
      this.displayStateChangeConfirmation = false;
    },
    yesNo,
    yesNoStateReversedColor,
  },
  async created() {
    await this.fetchEnumValues('DeferredReason');
  },
};
</script>
